import React from "react";
import { useNavigate } from "react-router-dom";
import "./AllUsersView.css";

/**
 * ADMIN – ALL USERS DASHBOARD (UNIFIED)
 *
 * Migration reference:
 * - ReviewAgentVerifications / ReviewScoutVerifications / ReviewClubVerifications → AdminRoleVerification
 * - UnderageMessagingRequestsReview → Active (verification-gated messaging)
 * - AgentContractReview / ClubContractReview → AdminContractReview
 * - PlayerContractReview → AdminFinalPlayerReport
 * - AgentReportReview / ScoutReportReview / ClubReportReview → AdminRoleReportReview
 * - PlayerCareerReporting (Player/Admin duplicate) → AdminPlayerCareerReview
 * - ReviewTransfermarktApplications → AdminTransferMarktReview
 */

const allUserComponents = [
    {
        title: "Refresh Youtube Videos",
        description:
            "Manually trigger the system to refresh and sync new Youtube video links so it can be accessed by users. We are doing it here to reduce API calls.",
        route: "/refresh-youtube-videos",
    },

    // ✅ Maintenance Mode
    {
        title: "Maintenance Mode",
        description:
            "Open the maintenance screen shown to users while the system is temporarily unavailable or being upgraded.",
        route: "/admin/maintenance",
    },

    // ✅ User Status Management
    {
        title: "Manage User Status",
        description:
            "Enable or disable user accounts from the Admin dashboard. Useful for compliance, testing, or account restrictions.",
        route: "/admin/manage-user-status",
    },

    // 🔎 NEW – User Lookup Tool
    {
        title: "User Lookup",
        description:
            "Search for any user across the platform by username or email for administrative review, investigation, or quick access.",
        route: "/admin/user-lookup",
    },

    // 📩 Admin Messaging
    {
        title: "Message Users",
        description:
            "Send broadcast messages to users by category (Players, Agents, Scouts, Clubs) from the Admin dashboard.",
        route: "/admin/message-users",
    },

    // 🔒 Manual Discoverability Control
    {
        title: "Limit Player Discoverability",
        description:
            "Manually search for players and control whether they appear in the video feed and discovery pool. Useful when a player signs without submitting a final contract report.",
        route: "/admin/limit-discoverability",
    },

    // 🔐 Verification & Compliance
    {
        title: "Unified Role Verification",
        description:
            "Review and approve verification requests submitted by Agents, Scouts, and Clubs from a single unified system.",
        route: "/admin/verifications",
    },

    // ✅ NEW — Video Review (Moderation)
    {
        title: "Video Review",
        description:
            "Review, approve, reject, or remove player-uploaded videos to prevent spam and non-football content from appearing in the scouting feed.",
        route: "/admin/video-review",
    },

    {
        title: "Underage Messaging Requests",
        description:
            "Review and manage messaging access involving underage players. Messaging is restricted to VERIFIED agents, scouts, and clubs and is enforced through platform compliance rules.",
        route: "/admin/underage-messaging-requests",
    },

    // 📄 Contracts
    {
        title: "Unified Contract Review",
        description:
            "Review, approve, and manage all submitted player, agent, and club contracts from a single unified dashboard.",
        route: "/admin/contracts",
    },
    {
        title: "Contract Termination",
        description:
            "View active contracts and terminate them early for testing, disputes, or exceptional cases.",
        route: "/admin/end-contracts",
    },

    // ✅ NEW — Live Contract Monitoring (Activated + Countdown Running)
    {
        title: "Contract Monitor",
        description:
            "Live feed of activated contracts with countdown timers. Track expiring contracts and payment/revenue status in real time.",
        route: "/admin/contract-monitor",
        live: true,
    },

    // 💰 NEW — Revenue Sharing
    {
        title: "Revenue Sharing",
        description:
            "View and manage revenue distribution across activated contracts. Monitor platform earnings, payout breakdowns, and financial splits.",
        route: "/admin/revenue-sharing",
    },

    // 📊 Reports
    {
        title: "Mark Signing Success & Final Contract Report",
        description:
            "Review final player contract reports and officially mark each signing as successful or unsuccessful.",
        route: "/admin/player-contract-reports",
    },
    {
        title: "Unified Report Review",
        description:
            "Read, verify, and manage reports submitted by Agents, Scouts, and Clubs regarding player development, performance, and progress.",
        route: "/admin/unified-reports",
    },

    // 🏆 Performance & Rankings
    {
        title: "Leaderboard",
        description:
            "View the platform leaderboard to see the highest-performing players based on verified ratings and eligibility rules. This reflects the same ranking model used across Scouts and Players.",
        route: "/admin/leaderboard",
    },
    {
        title: "Player Career Reporting",
        description:
            "View and review all submitted player career reports for verification, tracking, and administrative oversight.",
        route: "/admin/player-career-reports",
    },

    // 🧩 Platform Tools
    {
        title: "Review VSN Index Applications",
        description: "Review applications submitted by players to be listed on VSN Index.",
        route: "/admin/transfermarkt-applications",
    },

    // 🚀 BETA ACCESS
    {
        title: "Approve Beta Access",
        description:
            "Manually approve Early Bird Beta access for users who have completed payment outside the platform.",
        route: "/admin/approve-beta-access",
    },
];

const AllUsersView = () => {
    const navigate = useNavigate();

    return (
        <div className="all-users-dashboard-view">
            <div className="all-users-dashboard-grid">
                {allUserComponents.map((component, index) => (
                    <div className="admin-card" key={index}>
                        <div className="admin-card-title-row">
                            {component.live ? <span className="admin-live-dot" /> : null}
                            <h3>{component.title}</h3>
                        </div>

                        <p>{component.description}</p>

                        <button
                            className="admin-open-button"
                            onClick={() => navigate(component.route)}
                        >
                            Open
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AllUsersView;




















